import React from "react";
import { useTranslation } from "react-i18next";
import { useAdminStats } from "../../services/dashboardService";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  Users,
  FileText,
  Calendar,
  Briefcase,
  Building,
  DollarSign,
  MessageSquare,
  Mail,
  TrendingUp,
  Activity,
  AlertCircle,
  Award,
} from "lucide-react";

// Format large numbers
const formatNumber = (num: number): string => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
};

// Format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format month for display
const formatMonth = (monthStr: string): string => {
  const [year, month] = monthStr.split("-");
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { data: stats, isLoading, error } = useAdminStats();

  // Loading state with skeleton
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div className="h-10 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
            <div className="h-8 w-32 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-32 bg-white dark:bg-gray-800 rounded-xl shadow-sm animate-pulse"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <Card className="bg-white dark:bg-gray-800 border-red-200 dark:border-red-900">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {t("admin.dashboard.errors.loadingError")}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {error instanceof Error ? error.message : "Unknown error"}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <Card className="bg-white dark:bg-gray-800">
            <CardContent className="flex items-center justify-center py-12">
              <p className="text-gray-500 dark:text-gray-400">
                {t("admin.dashboard.errors.noData")}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const overviewStats = stats.overview || {};
  const contentStats = stats.content || {};
  const engagementStats = stats.engagement || {};
  const financialStats = stats.financial || {};
  const recentActivity = stats.recent_activity || {};

  // Prepare chart data
  const postsChartData = contentStats.posts_by_month?.slice().reverse() || [];
  const eventsChartData = contentStats.events_by_month?.slice().reverse() || [];
  const donationsChartData =
    financialStats.donations_by_month?.slice().reverse() || [];

  const projectsStatusData = Object.entries(
    contentStats.projects_by_status || {}
  ).map(([status, count]) => ({
    name: status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" "),
    value: count as number,
  }));

  const COLORS = [
    "hsl(var(--primary))",
    "hsl(142, 76%, 36%)",
    "hsl(221, 83%, 53%)",
    "hsl(280, 100%, 70%)",
    "hsl(0, 84%, 60%)",
  ];

  // Stat cards configuration
  const statCards = [
    {
      title: t("admin.dashboard.stats.totalUsers"),
      value: overviewStats.total_users || 0,
      subtitle: `${overviewStats.active_users || 0} ${t(
        "admin.dashboard.stats.active"
      )}`,
      icon: Users,
      gradient: "from-blue-500 to-cyan-500",
      bgColor: "bg-blue-50 dark:bg-blue-900/20",
      iconColor: "text-blue-600 dark:text-blue-400",
    },
    {
      title: t("admin.dashboard.stats.posts"),
      value: overviewStats.total_posts || 0,
      subtitle: `${overviewStats.published_posts || 0} ${t(
        "admin.dashboard.stats.published"
      )}`,
      icon: FileText,
      gradient: "from-purple-500 to-pink-500",
      bgColor: "bg-purple-50 dark:bg-purple-900/20",
      iconColor: "text-purple-600 dark:text-purple-400",
    },
    {
      title: t("admin.dashboard.stats.events"),
      value: overviewStats.total_events || 0,
      subtitle: `${overviewStats.upcoming_events || 0} ${t(
        "admin.dashboard.stats.upcoming"
      )}`,
      icon: Calendar,
      gradient: "from-green-500 to-emerald-500",
      bgColor: "bg-green-50 dark:bg-green-900/20",
      iconColor: "text-green-600 dark:text-green-400",
    },
    {
      title: t("admin.dashboard.stats.projects"),
      value: overviewStats.total_projects || 0,
      subtitle: `${overviewStats.active_projects || 0} ${t(
        "admin.dashboard.stats.active"
      )}`,
      icon: Briefcase,
      gradient: "from-orange-500 to-red-500",
      bgColor: "bg-orange-50 dark:bg-orange-900/20",
      iconColor: "text-orange-600 dark:text-orange-400",
    },
    {
      title: t("admin.dashboard.stats.scholarships"),
      value: overviewStats.total_scholarships || 0,
      subtitle: `${overviewStats.active_scholarships || 0} ${t(
        "admin.dashboard.stats.active"
      )}`,
      icon: Award,
      gradient: "from-teal-500 to-cyan-500",
      bgColor: "bg-teal-50 dark:bg-teal-900/20",
      iconColor: "text-teal-600 dark:text-teal-400",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              {t("admin.dashboard.title")}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Overview of your platform statistics
            </p>
          </div>
          <Badge
            variant="secondary"
            className="px-4 py-2 text-sm bg-gradient-to-r from-blue-500 to-cyan-500 text-white border-0 shadow-lg"
          >
            <Activity className="w-4 h-4 mr-2 animate-pulse" />
            {t("admin.dashboard.liveData")}
          </Badge>
        </div>

        {/* Overview Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className="bg-white dark:bg-gray-800 border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                <div
                  className={`absolute top-0 right-0 w-32 h-32 ${stat.bgColor} rounded-full -mr-16 -mt-16 opacity-50 group-hover:opacity-75 transition-opacity`}
                />
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
                  <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {stat.title}
                  </CardTitle>
                  <div
                    className={`p-2 rounded-lg ${stat.bgColor} group-hover:scale-110 transition-transform`}
                  >
                    <Icon className={`h-5 w-5 ${stat.iconColor}`} />
                  </div>
                </CardHeader>
                <CardContent className="relative z-10">
                  <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                    {formatNumber(stat.value)}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    {stat.subtitle}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Financial Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-md hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">
                {t("admin.dashboard.charts.donationsOverTime")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {donationsChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={donationsChartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="opacity-30"
                    />
                    <XAxis
                      dataKey="month"
                      tickFormatter={formatMonth}
                      className="text-xs"
                    />
                    <YAxis className="text-xs" />
                    <Tooltip
                      formatter={(value: number) => [
                        `XAF ${formatCurrency(value)}`,
                        "Amount",
                      ]}
                      labelFormatter={(label) => formatMonth(label)}
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                      }}
                    />
                    <Bar
                      dataKey="total_amount"
                      fill="url(#colorGradient)"
                      radius={[8, 8, 0, 0]}
                    />
                    <defs>
                      <linearGradient
                        id="colorGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#06b6d4" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-400">
                  No donation data available
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-0 shadow-md hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">
                {t("admin.dashboard.charts.financialSummary")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
                      <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                        {t("admin.dashboard.stats.totalDonations")}
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        XAF{" "}
                        {formatCurrency(
                          financialStats.total_donation_amount || 0
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                      <Building className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                        {t("admin.dashboard.stats.listingPayments")}
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        XAF{" "}
                        {formatCurrency(
                          financialStats.total_listing_payment_amount || 0
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    {t("admin.dashboard.stats.donationCount")}
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {financialStats.total_donations || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    {t("admin.dashboard.stats.paymentCount")}
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {financialStats.total_listing_payments || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Engagement Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              title: t("admin.dashboard.stats.contacts"),
              value: engagementStats.total_contacts || 0,
              subtitle: `${engagementStats.unhandled_contacts || 0} ${t(
                "admin.dashboard.stats.unhandled"
              )}`,
              icon: MessageSquare,
              gradient: "from-indigo-500 to-purple-500",
            },
            {
              title: t("admin.dashboard.stats.newsletter"),
              value: engagementStats.total_newsletter_subscribers || 0,
              subtitle: `${
                engagementStats.active_newsletter_subscribers || 0
              } ${t("admin.dashboard.stats.active")}`,
              icon: Mail,
              gradient: "from-pink-500 to-rose-500",
            },
            {
              title: t("admin.dashboard.stats.campaigns"),
              value: engagementStats.total_campaigns || 0,
              subtitle: `${engagementStats.active_campaigns || 0} ${t(
                "admin.dashboard.stats.active"
              )}`,
              icon: TrendingUp,
              gradient: "from-amber-500 to-orange-500",
            },
          ].map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className="bg-white dark:bg-gray-800 border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className={`h-1 bg-gradient-to-r ${stat.gradient}`} />
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {stat.title}
                  </CardTitle>
                  <Icon className="h-5 w-5 text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                    {formatNumber(stat.value)}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {stat.subtitle}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Content Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-md hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">
                {t("admin.dashboard.charts.postsOverTime")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {postsChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={postsChartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="opacity-30"
                    />
                    <XAxis
                      dataKey="month"
                      tickFormatter={formatMonth}
                      className="text-xs"
                    />
                    <YAxis className="text-xs" />
                    <Tooltip
                      labelFormatter={(label) => formatMonth(label)}
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#8b5cf6"
                      strokeWidth={3}
                      dot={{ fill: "#8b5cf6", r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-400">
                  No post data available
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-0 shadow-md hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">
                {t("admin.dashboard.charts.eventsOverTime")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {eventsChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={eventsChartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="opacity-30"
                    />
                    <XAxis
                      dataKey="month"
                      tickFormatter={formatMonth}
                      className="text-xs"
                    />
                    <YAxis className="text-xs" />
                    <Tooltip
                      labelFormatter={(label) => formatMonth(label)}
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#10b981"
                      strokeWidth={3}
                      dot={{ fill: "#10b981", r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-400">
                  No event data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Projects Status and Business Listings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-md hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">
                {t("admin.dashboard.charts.projectsByStatus")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {projectsStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={projectsStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(props: {
                        name?: string;
                        percent?: number;
                        [key: string]: unknown;
                      }) => {
                        const name = props.name || "";
                        const percent = (props.percent as number) || 0;
                        return percent > 0.05
                          ? `${name}: ${(percent * 100).toFixed(0)}%`
                          : "";
                      }}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {projectsStatusData.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                      }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: "20px" }}
                      formatter={(value) => (
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {value}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-400">
                  No project data available
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-0 shadow-md hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">
                {t("admin.dashboard.stats.totalListings")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {t("admin.dashboard.stats.totalListings")}
                  </span>
                  <span className="text-3xl font-bold text-gray-900 dark:text-white">
                    {overviewStats.total_business_listings || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {t("admin.dashboard.stats.activeListings")}
                  </span>
                  <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                    {overviewStats.active_business_listings || 0}
                  </span>
                </div>
                {contentStats.business_listings_by_plan &&
                  contentStats.business_listings_by_plan.length > 0 && (
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                        {t("admin.dashboard.stats.byPlan")}
                      </h4>
                      <div className="space-y-2">
                        {contentStats.business_listings_by_plan.map(
                          (
                            plan: { plan_name: string; count: number },
                            index: number
                          ) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                            >
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {plan.plan_name}
                              </span>
                              <Badge
                                variant="secondary"
                                className="font-semibold"
                              >
                                {plan.count}
                              </Badge>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="bg-white dark:bg-gray-800 border-0 shadow-md hover:shadow-xl transition-all duration-300">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              {t("admin.dashboard.recentActivity.title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {recentActivity.recent_users?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {t("admin.dashboard.recentActivity.recentUsers")}
                  </h4>
                  <div className="space-y-2">
                    {recentActivity.recent_users.map(
                      (user: {
                        id: number;
                        name: string;
                        created_at: string;
                      }) => (
                        <div
                          key={user.id}
                          className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1">
                            {user.name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            {new Date(user.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {recentActivity.recent_posts?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    {t("admin.dashboard.recentActivity.recentPosts")}
                  </h4>
                  <div className="space-y-2">
                    {recentActivity.recent_posts.map(
                      (post: { id: number; title: string; status: string }) => (
                        <div
                          key={post.id}
                          className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1">
                            {post.title}
                          </span>
                          <Badge
                            variant={
                              post.status === "published"
                                ? "default"
                                : "secondary"
                            }
                            className="text-xs ml-2"
                          >
                            {post.status}
                          </Badge>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {recentActivity.recent_events?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {t("admin.dashboard.recentActivity.recentEvents")}
                  </h4>
                  <div className="space-y-2">
                    {recentActivity.recent_events.map(
                      (event: {
                        id: number;
                        title: string;
                        start_date?: string;
                        starts_at?: string;
                      }) => (
                        <div
                          key={event.id}
                          className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1">
                            {event.title}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            {new Date(
                              event.start_date || event.starts_at || ""
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {recentActivity.recent_contacts?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    {t("admin.dashboard.recentActivity.recentContacts")}
                  </h4>
                  <div className="space-y-2">
                    {recentActivity.recent_contacts.map(
                      (contact: {
                        id: number;
                        subject: string;
                        created_at: string;
                      }) => (
                        <div
                          key={contact.id}
                          className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1">
                            {contact.subject}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            {new Date(contact.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {!recentActivity.recent_users?.length &&
                !recentActivity.recent_posts?.length &&
                !recentActivity.recent_events?.length &&
                !recentActivity.recent_contacts?.length && (
                  <div className="col-span-full text-center py-8 text-gray-400">
                    No recent activity
                  </div>
                )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
